import { z } from "zod";
import type { BaseUserResponse } from "../../auth/data";

export const PROFILE_GENDERS = ["male", "female", "other"] as const;

export type ProfileGender = (typeof PROFILE_GENDERS)[number];

export const isProfileGender = (value: unknown): value is ProfileGender =>
  typeof value === "string" && (PROFILE_GENDERS as readonly string[]).includes(value);

/** Fixed UI country prefix, so the backend receives digits only. */
export const PROFILE_PHONE_DIGIT_COUNT = 10;

/** Mirrors the 2MB hint in profile.image_format_hint. */
export const MAX_PROFILE_AVATAR_SIZE_BYTES = 2 * 1024 * 1024;

export const ACCEPTED_PROFILE_AVATAR_TYPES = ["image/jpeg", "image/png", "image/gif"] as const;

export interface ProfileFormValidationMessages {
  firstNameMinLength: string;
  lastNameMinLength: string;
  phoneNumberInvalid: string;
  avatarTooLarge: string;
  avatarInvalidType: string;
  genderInvalid: string;
}

export const createProfileFormSchema = (messages: ProfileFormValidationMessages) =>
  z.object({
    firstName: z.string().trim().min(2, messages.firstNameMinLength),
    lastName: z.string().trim().min(2, messages.lastNameMinLength),
    phoneNumber: z
      .string()
      .trim()
      .refine(
        (value) => value === "" || new RegExp(`^\\d{${PROFILE_PHONE_DIGIT_COUNT}}$`).test(value),
        { message: messages.phoneNumberInvalid },
      ),
    profileImageFile: z
      .instanceof(File, { message: messages.avatarInvalidType })
      .nullable()
      .refine(
        (file) =>
          file === null || (ACCEPTED_PROFILE_AVATAR_TYPES as readonly string[]).includes(file.type),
        { message: messages.avatarInvalidType },
      )
      .refine((file) => file === null || file.size <= MAX_PROFILE_AVATAR_SIZE_BYTES, {
        message: messages.avatarTooLarge,
      }),
    role: z.string(),
    gender: z.enum(PROFILE_GENDERS, { message: messages.genderInvalid }),
  });

export type ProfileFormValues = z.infer<ReturnType<typeof createProfileFormSchema>>;

export const toProfileFormDefaults = (user: BaseUserResponse | null): ProfileFormValues => {
  const gender = user?.gender;

  return {
    firstName: user?.first_name ?? "",
    lastName: user?.last_name ?? "",
    phoneNumber: user?.phone_number ?? "",
    profileImageFile: null,
    role: user?.role ?? "admin",
    gender: isProfileGender(gender) ? gender : "other",
  };
};
